import LogUtil from '../../../core/utils/LogUtil';

/**
 * Coupon Service
 * 쿠폰 관리 비즈니스 로직 계층
 */
export class CouponService {
  constructor(couponRepository) {
    this.couponRepository = couponRepository;
    LogUtil.log('🔧 [CouponService] 초기화 완료');
  }

  /**
   * 쿠폰 생성
   * @param {Object} data - 쿠폰 생성 데이터
   * @returns {Promise<BaseResponse<any>>}
   */
  async createCoupon(data) {
    try {
      LogUtil.log('📤 [CouponService] 쿠폰 생성 요청:', data);

      if (!data.pharmacy_id) {
        throw new Error('약국 ID가 필요합니다.');
      }
      if (!data.coupon_type) {
        throw new Error('쿠폰 타입이 필요합니다.');
      }
      if (data.amount === undefined || data.amount === null) {
        throw new Error('금액이 필요합니다.');
      }

      const result = await this.couponRepository.createCoupon(data);
      LogUtil.log('📥 [CouponService] 쿠폰 생성 성공:', result);
      return result;
    } catch (error) {
      LogUtil.error('❌ [CouponService] 쿠폰 생성 실패:', error);
      throw error;
    }
  }

  /**
   * 쿠폰 목록 조회
   * @param {Object} params - 쿼리 파라미터
   * @returns {Promise<BaseResponse<any>>}
   */
  async getCoupons(params = {}) {
    try {
      LogUtil.log('📤 [CouponService] 쿠폰 목록 조회 요청:', params);
      const result = await this.couponRepository.getCoupons(params);
      LogUtil.log('📥 [CouponService] 쿠폰 목록 조회 성공:', result);
      return result;
    } catch (error) {
      LogUtil.error('❌ [CouponService] 쿠폰 목록 조회 실패:', error);
      throw error;
    }
  }

  /**
   * 약국별 쿠폰 목록 조회
   * @param {number} pharmacyId - 약국 ID
   * @param {Object} params - 추가 쿼리 파라미터
   * @returns {Promise<BaseResponse<any>>}
   */
  async getCouponsByPharmacyId(pharmacyId, params = {}) {
    try {
      LogUtil.log('📤 [CouponService] 약국별 쿠폰 목록 조회:', { pharmacyId, params });

      if (!pharmacyId) {
        throw new Error('약국 ID가 필요합니다.');
      }

      const result = await this.couponRepository.getCoupons({
        pharmacy_id: pharmacyId,
        ...params,
      });
      LogUtil.log('📥 [CouponService] 약국별 쿠폰 목록 조회 성공:', result);
      return result;
    } catch (error) {
      LogUtil.error('❌ [CouponService] 약국별 쿠폰 목록 조회 실패:', error);
      throw error;
    }
  }

  /**
   * 쿠폰 상세 조회
   * @param {number} couponId - 쿠폰 ID
   * @returns {Promise<BaseResponse<any>>}
   */
  async getCouponById(couponId) {
    try {
      LogUtil.log('📤 [CouponService] 쿠폰 상세 조회 요청:', couponId);

      if (!couponId) {
        throw new Error('쿠폰 ID가 필요합니다.');
      }

      const result = await this.couponRepository.getCouponById(couponId);
      LogUtil.log('📥 [CouponService] 쿠폰 상세 조회 성공:', result);
      return result;
    } catch (error) {
      LogUtil.error('❌ [CouponService] 쿠폰 상세 조회 실패:', error);
      throw error;
    }
  }

  /**
   * 사용자별 쿠폰 목록 조회
   * @param {number} userId - 사용자 ID
   * @param {Object} params - 쿼리 파라미터
   * @returns {Promise<BaseResponse<any>>}
   */
  async getCouponsByUserId(userId, params = {}) {
    try {
      LogUtil.log('📤 [CouponService] 사용자별 쿠폰 조회 요청:', { userId, params });

      if (!userId) {
        throw new Error('사용자 ID가 필요합니다.');
      }

      const result = await this.couponRepository.getCouponsByUserId(userId, params);
      LogUtil.log('📥 [CouponService] 사용자별 쿠폰 조회 성공:', result);
      return result;
    } catch (error) {
      LogUtil.error('❌ [CouponService] 사용자별 쿠폰 조회 실패:', error);
      throw error;
    }
  }

  /**
   * 타겟팅별 대상자 수 조회
   * @param {string} pharmacyId - 약국 사업자등록번호
   * @param {Object} params - 쿼리 파라미터
   * @returns {Promise<BaseResponse<any>>}
   */
  async getEligibleCount(pharmacyId, params = {}) {
    try {
      LogUtil.log('📤 [CouponService] 대상자 수 조회 요청:', { pharmacyId, params });

      if (!pharmacyId) {
        throw new Error('약국 사업자등록번호가 필요합니다.');
      }

      const result = await this.couponRepository.getEligibleCount(pharmacyId, params);
      LogUtil.log('📥 [CouponService] 대상자 수 조회 성공:', result);
      return result;
    } catch (error) {
      LogUtil.error('❌ [CouponService] 대상자 수 조회 실패:', error);
      throw error;
    }
  }

  /**
   * 약국별 쿠폰 발행/사용 내역 조회
   * @param {string} pharmacyId - 약국 사업자등록번호
   * @param {Object} params - 쿼리 파라미터
   * @returns {Promise<BaseResponse<any>>}
   */
  async getPharmacyHistory(pharmacyId, params = {}) {
    try {
      LogUtil.log('📤 [CouponService] 약국 쿠폰 내역 조회:', { pharmacyId, params });

      if (!pharmacyId) {
        throw new Error('약국 사업자등록번호가 필요합니다.');
      }

      const result = await this.couponRepository.getPharmacyHistory(pharmacyId, params);
      LogUtil.log('📥 [CouponService] 약국 쿠폰 내역 조회 성공:', result);
      return result;
    } catch (error) {
      LogUtil.error('❌ [CouponService] 약국 쿠폰 내역 조회 실패:', error);
      throw error;
    }
  }
}
